import { FormInstance } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { ProjectId, SpaceId, SpaceTabId } from 'api/models';
import { Project } from 'api/project';
import { Space, SpaceTabDocumentType } from 'api/space';
import {
  CreateSpaceEmbedPayload,
  CreateSpaceFromTemplatepayload,
  CreateSpaceTabPayload,
} from 'api/space/space-tab';
import { toastService } from 'components/molecules/toast';
import { setActiveTabKey } from 'data/space-tabs';
import { getActiveTabKey } from 'data/space-tabs/selectors';
import { TemplateId } from 'data/templates/api';
import { useProjectSpacePermission } from 'hooks/permissions';
import { useTranslate } from 'hooks/use-translation';
import { getProjectSpacesQueryKey, useProjectSpacesQuery } from 'queries';
import { useSpaceTabCreateMutation } from 'queries/project-spaces';
import { getSpaceDetailUrl, getSpaceTabDetailUrl } from 'utils/urls';

export type SpaceActionTypes = {
  project: Project;
  spaceId: string;
  spaceTabId: string;
};

export enum CreationType {
  Doc = 'doc',
  Embed = 'embed',
  Template = 'template',
}

type DocCreationType = {
  type: CreationType.Doc;
  payload: {
    spaceId: number;
  };
};

type EmbedCreationType = {
  type: CreationType.Embed;
  payload: {
    spaceId: number;
    url: string;
    form: FormInstance<any>;
  };
};

type FromTemplateCreationType = {
  type: CreationType.Template;
  payload: {
    spaceId: number;
    templateId: TemplateId;
  };
};

const DEFAULT_TAB_TITLE = 'Untitled';

export const useSpaceActions = ({
  project,
  spaceId,
  spaceTabId,
}: SpaceActionTypes) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const history = useHistory();
  const translate = useTranslate();

  const { projectId, projectName } = project;
  const [creatingDocumentType, setcreatingDocumentType] =
    useState<CreationType>();
  const { canCreate } = useProjectSpacePermission(ProjectId(projectId));

  const { data: projectSpacesList = [], status: projectSpacesFetchStatus } =
    useProjectSpacesQuery(ProjectId(projectId), {
      onSuccess: (spaces) => {
        const currentSpace = spaces[0];

        // when user enters 'new' after space then check the apces availability and redirect
        // setQueryData re-triggers this on success
        if (spaceId === 'new' && currentSpace && currentSpace.spaceId !== 0) {
          history.push(
            getSpaceDetailUrl(
              SpaceId(Number(projectId), Number(currentSpace.spaceId)),
            ),
          );
        }
      },
    });

  const { mutate: createSpaceTabMutation, isLoading: creatingTabDocument } =
    useSpaceTabCreateMutation({
      onError: () => {
        toastService({
          title: translate('error'),
          message: translate('projects.spaces.create.failure'),
        }).error();
      },
    });

  const currentSpaceDetails = useMemo(() => {
    return projectSpacesList.find((space) => space.spaceId === Number(spaceId));
  }, [projectSpacesList, spaceId]);

  const onSpaceDocCreate = (
    data: DocCreationType | EmbedCreationType | FromTemplateCreationType,
  ) => {
    setcreatingDocumentType(data.type);

    switch (data.type) {
      case 'doc':
      default: {
        const {
          payload: { spaceId: updatedSpaceId },
        } = data as DocCreationType;

        const payload: CreateSpaceTabPayload = {
          spaceTabType: 'ROCKETLANE_DOCUMENT' as SpaceTabDocumentType, // TODO: @krishna change this
          spaceTabTitle: DEFAULT_TAB_TITLE,
          space: { spaceId: updatedSpaceId },
        };

        return createSpaceTabMutation(payload, {
          onSuccess: async (response) => {
            await queryClient.invalidateQueries(
              getProjectSpacesQueryKey(ProjectId(projectId)),
            );

            setcreatingDocumentType(undefined);

            return history.push(
              getSpaceTabDetailUrl(
                SpaceTabId<number>(
                  projectId,
                  updatedSpaceId,
                  response.spaceTabId,
                ),
              ),
            );
          },
        });
      }

      case 'embed': {
        const {
          payload: { spaceId: updatedSpaceId, url, form },
        } = data as EmbedCreationType;

        const payload: CreateSpaceEmbedPayload = {
          url,
          space: { spaceId: updatedSpaceId },
        };

        return createSpaceTabMutation(payload, {
          onSuccess: async (response) => {
            form.resetFields();

            await queryClient.invalidateQueries(
              getProjectSpacesQueryKey(ProjectId(projectId)),
            );

            setcreatingDocumentType(undefined);

            return history.push(
              getSpaceTabDetailUrl(
                SpaceTabId<number>(
                  projectId,
                  updatedSpaceId,
                  response.spaceTabId,
                ),
              ),
            );
          },
        });
      }

      case 'template': {
        const {
          payload: { spaceId: updatedSpaceId, templateId },
        } = data as FromTemplateCreationType;

        const payload: CreateSpaceFromTemplatepayload = {
          space: { spaceId: updatedSpaceId },
          entitySource: { sourceId: templateId },
        };

        return createSpaceTabMutation(payload, {
          onSuccess: async (response) => {
            await queryClient.invalidateQueries(
              getProjectSpacesQueryKey(ProjectId(projectId)),
            );

            setcreatingDocumentType(undefined);

            return history.push(
              getSpaceTabDetailUrl(
                SpaceTabId<number>(
                  projectId,
                  updatedSpaceId,
                  response.spaceTabId,
                ),
              ),
            );
          },
        });
      }
    }
  };

  const onSpaceAdd = (isPrivate?: boolean) => {
    const hasNewSpace = projectSpacesList.find(
      (space) => space.private === isPrivate && space.spaceId === 0,
    );

    if (hasNewSpace) {
      return false;
    }

    const newSpace: Space = {
      spaceId: 0,
      private: isPrivate ?? false,
      spaceName: '',
      spaceTabs: [],
      project: {
        projectId,
        projectName,
      },
    };
    const updatedSpaces = [...projectSpacesList, newSpace];

    return queryClient.setQueryData(
      getProjectSpacesQueryKey(ProjectId(projectId)),
      updatedSpaces,
    );
  };

  const activeSpaceTabKey = useSelector(getActiveTabKey);

  useEffect(() => {
    if (activeSpaceTabKey !== spaceTabId) {
      dispatch(setActiveTabKey(spaceTabId));
    }
  }, [dispatch, spaceTabId, activeSpaceTabKey]);

  return {
    currentSpaceDetails,
    projectSpacesList,
    projectSpacesFetchStatus,
    creatingTabDocument,
    creatingDocumentType,
    onSpaceDocCreate,
    onSpaceAdd,
    canCreate,
  };
};
